// Vendors
import { cookies } from '@openbank/cf-ui-architecture-core';
// Enumerations
import { StorageCookiesEnum } from '@openbank/ziniaweb-ui-core-models';
import { WebsocketErrorEnumeration } from './enumerations/websocket-error.enumeration';
// Classes
import { WebSocketsClass } from 'classes/websocket/websocket';
// Constants
import { WEBSOCKET_SEND_MESSAGE_CONFIGURATION } from './utils/constants/websocket.handlers.utils.constants';
// Types
import {
  WebSocketBuilderHandlersType,
  WebSocketConnectBuilderHandlersType,
  WebSocketDisconnectBuilderHandlersType,
  WebSocketMethodsHandlersType,
  WebSocketSendBuilderHandlersType,
} from './types/websocket.handlers.type';
// Utilities
import { getLendingWebsocketUrl } from './utils/websocket.handlers.utils';

const connectWebSocketChannelEventHandler = async ({
  websocketRef,
  setWebsocketConnected,
  handlers,
}: WebSocketConnectBuilderHandlersType): Promise<void> => {
  try {
    websocketRef.current = new WebSocketsClass({ url: getLendingWebsocketUrl() });
    websocketRef.current.listen(handlers);
    await websocketRef.current.connect();
    setWebsocketConnected(true);
  } catch (error) {
    throw new Error(WebsocketErrorEnumeration.WEBSOCKET_ERROR_CONNECTION);
  }
  return Promise.resolve();
};

const disconnectWebSocketFromChannelEventHandler = async ({
  websocketRef,
}: WebSocketDisconnectBuilderHandlersType): Promise<void> => {
  try {
    await websocketRef?.current?.disconnect();
  } catch (error) {
    throw new Error(WebsocketErrorEnumeration.WEBSOCKET_ERROR_DESCONNECTION);
  }
};

const sendWebSocketMessageEventHandler = ({
  websocketRef,
  endpoint,
  payload,
}: WebSocketSendBuilderHandlersType): void => {
  try {
    const parsedMessage = JSON.stringify({
      ...WEBSOCKET_SEND_MESSAGE_CONFIGURATION,
      endpoint,
      detail: {
        headers: {
          accessToken: cookies.getCookie(StorageCookiesEnum.ACCESS_TOKEN),
        },
        payload,
      },
    });
    websocketRef?.current?.send(parsedMessage);
  } catch {
    throw new Error(WebsocketErrorEnumeration.WEBSOCKET_NOT_AVAILABLE);
  }
};

const WebSocketHandlers = ({
  websocketRef,
  setWebsocketConnected,
}: WebSocketBuilderHandlersType): WebSocketMethodsHandlersType => ({
  handleConnectWebSocketChannelEvent: handlers =>
    connectWebSocketChannelEventHandler({ websocketRef, setWebsocketConnected, handlers }),
  handleDisconnectWebSocketFromChannelEvent: () =>
    disconnectWebSocketFromChannelEventHandler({ websocketRef }),
  handleSendWebSocketMessageEvent: ({ endpoint, payload }) =>
    sendWebSocketMessageEventHandler({ endpoint, payload, websocketRef }),
});

export { WebSocketHandlers };
